import ScrollMagic from "scrollmagic";

document.addEventListener("DOMContentLoaded", () => {
	const hero = document.querySelector(".solutionsHero2025");

	if (!hero) {
		return;
	}

	const findDetachedCta = () => {
		const next = hero.nextElementSibling;
		if (next && next.classList.contains("solutionsHero2025__cta")) {
			return next;
		}
		return document.querySelector(".solutionsHero2025__cta");
	};

	const cta = findDetachedCta();

	if (!cta) {
		return;
	}

	const ctaButton = cta.querySelector(".solutionsHero2025__cta-button");
	const stickyTrigger = hero.querySelector(".solutionsHero2025__eyebrow") || hero.querySelector(".solutionsHero2025__title") || hero;
	const DESKTOP_BREAKPOINT = 1280;
	const STICKY_OFFSET = 300;
	const TRIGGER_FADE_DISTANCE = 160;
	const TRIGGER_HIDE_DISTANCE = 60;

	const normalizeTrigger = (value) => {
		if (!value) {
			return "";
		}

		return value.replace(/^\./, "").trim();
	};

	const triggerClass = normalizeTrigger((ctaButton && ctaButton.dataset && ctaButton.dataset.triggerClass) || hero.dataset.scrollTrigger) || "solutionScrollCarousel__cta";
	const triggerTarget = document.querySelector(`.${triggerClass}`);

	const originalParent = cta.parentNode;
	const originalNextSibling = cta.nextSibling;

	let coverProgress = 0;
	let stickyActive = false;
	let heroVisible = false;
	let desktopBehaviorEnabled = false;
	let currentLayoutIsDesktop = null;

	let heroObserver = null;
	let heroFallbackScrollHandler = null;
	let heroFallbackResizeHandler = null;
	let heroFallbackTicking = false;

	let triggerFallbackScrollHandler = null;
	let triggerFallbackResizeHandler = null;
	let triggerFallbackTicking = false;

	let scrollMagicController = null;
	let scrollMagicScene = null;

	let stickyFallbackScrollHandler = null;
	let stickyFallbackResizeHandler = null;
	let stickyFallbackTicking = false;

	const isDesktopViewport = () => window.innerWidth >= DESKTOP_BREAKPOINT;

	const isElementInViewport = (element) => {
		if (!element) {
			return false;
		}
		const rect = element.getBoundingClientRect();
		return rect.bottom > 0 && rect.top < window.innerHeight;
	};

	const moveCtaToHero = () => {
		if (!hero.contains(cta)) {
			hero.appendChild(cta);
		}
	};

	const moveCtaToOriginalPosition = () => {
		if (cta.parentNode === originalParent) {
			return;
		}
		if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
			originalParent.insertBefore(cta, originalNextSibling);
		} else {
			originalParent.appendChild(cta);
		}
	};

	const cleanupHeroVisibility = () => {
		if (heroObserver) {
			heroObserver.disconnect();
			heroObserver = null;
		}
		if (heroFallbackScrollHandler) {
			window.removeEventListener("scroll", heroFallbackScrollHandler);
			window.removeEventListener("resize", heroFallbackResizeHandler);
			heroFallbackScrollHandler = null;
			heroFallbackResizeHandler = null;
			heroFallbackTicking = false;
		}
	};

	const cleanupTriggerVisibility = () => {
		if (triggerFallbackScrollHandler) {
			window.removeEventListener("scroll", triggerFallbackScrollHandler);
			window.removeEventListener("resize", triggerFallbackResizeHandler);
			triggerFallbackScrollHandler = null;
			triggerFallbackResizeHandler = null;
			triggerFallbackTicking = false;
		}
	};

	const cleanupStickyTracking = () => {
		if (scrollMagicScene) {
			scrollMagicScene.destroy(true);
			scrollMagicScene = null;
		}
		if (scrollMagicController) {
			scrollMagicController.destroy(true);
			scrollMagicController = null;
		}
		if (stickyFallbackScrollHandler) {
			window.removeEventListener("scroll", stickyFallbackScrollHandler);
			window.removeEventListener("resize", stickyFallbackResizeHandler);
			stickyFallbackScrollHandler = null;
			stickyFallbackResizeHandler = null;
			stickyFallbackTicking = false;
		}
	};

	const updateVisibility = () => {
		if (!desktopBehaviorEnabled) {
			return;
		}
		const shouldHide = !stickyActive || coverProgress >= 1 || !heroVisible;
		cta.classList.toggle("is-hidden", shouldHide);
	};

	const setStickyState = (shouldStick) => {
		if (!desktopBehaviorEnabled || stickyActive === shouldStick) {
			return;
		}
		stickyActive = shouldStick;
		cta.classList.toggle("is-fixed", stickyActive);
		updateVisibility();
	};

	const setCoverProgress = (value) => {
		const clamped = Math.min(Math.max(value, 0), 1);
		if (Math.abs(clamped - coverProgress) < 0.01) {
			return clamped;
		}
		coverProgress = clamped;
		cta.style.setProperty("--cta-cover-progress", clamped.toString());
		updateVisibility();
		return clamped;
	};

	const resetCoverProgress = () => {
		coverProgress = 0;
		cta.style.setProperty("--cta-cover-progress", "0");
		updateVisibility();
	};

	const updateTriggerCoveringState = () => {
		if (!desktopBehaviorEnabled || !triggerTarget) {
			resetCoverProgress();
			return;
		}

		const rect = triggerTarget.getBoundingClientRect();
		const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
		const ctaHeight = cta.offsetHeight || 0;
		const ctaTop = viewportHeight - ctaHeight;

		const overlap = ctaTop - rect.top;
		const fadeProgressRaw = (overlap + TRIGGER_FADE_DISTANCE) / (TRIGGER_FADE_DISTANCE + TRIGGER_HIDE_DISTANCE);
		setCoverProgress(fadeProgressRaw);
	};

	const observeTrigger = () => {
		cleanupTriggerVisibility();

		if (!triggerTarget) {
			resetCoverProgress();
			return;
		}

		const evaluate = () => {
			if (!desktopBehaviorEnabled) {
				triggerFallbackTicking = false;
				resetCoverProgress();
				return;
			}
			updateTriggerCoveringState();
			triggerFallbackTicking = false;
		};

		triggerFallbackScrollHandler = () => {
			if (triggerFallbackTicking) {
				return;
			}
			triggerFallbackTicking = true;
			window.requestAnimationFrame(evaluate);
		};

		triggerFallbackResizeHandler = triggerFallbackScrollHandler;

		window.addEventListener("scroll", triggerFallbackScrollHandler, { passive: true });
		window.addEventListener("resize", triggerFallbackResizeHandler);

		evaluate();
	};

	const observeHeroVisibility = () => {
		cleanupHeroVisibility();

		if (typeof IntersectionObserver === "undefined") {
			const evaluate = () => {
				heroVisible = isElementInViewport(hero);
				updateVisibility();
				heroFallbackTicking = false;
			};

			heroFallbackScrollHandler = () => {
				if (!desktopBehaviorEnabled || heroFallbackTicking) {
					return;
				}
				heroFallbackTicking = true;
				window.requestAnimationFrame(evaluate);
			};

			heroFallbackResizeHandler = heroFallbackScrollHandler;

			window.addEventListener("scroll", heroFallbackScrollHandler, { passive: true });
			window.addEventListener("resize", heroFallbackResizeHandler);

			evaluate();
			return;
		}

		heroObserver = new IntersectionObserver(
			(entries) => {
				heroVisible = entries.some((entry) => entry.isIntersecting);
				updateVisibility();
			},
			{
				root: null,
				threshold: [0, 0.1],
			}
		);

		heroObserver.observe(hero);
	};

	const initScrollMagicSticky = () => {
		if (typeof ScrollMagic === "undefined" || !stickyTrigger) {
			return false;
		}

		scrollMagicController = new ScrollMagic.Controller();

		scrollMagicScene = new ScrollMagic.Scene({
			triggerElement: stickyTrigger,
			triggerHook: 0,
			offset: -STICKY_OFFSET,
		})
			.addTo(scrollMagicController)
			.on("enter", (event) => {
				if (event.scrollDirection === "REVERSE") {
					setStickyState(false);
				} else {
					setStickyState(true);
				}
			})
			.on("leave", (event) => {
				if (event.scrollDirection === "REVERSE") {
					setStickyState(false);
				}
			});

		scrollMagicScene.refresh();

		const rect = stickyTrigger.getBoundingClientRect();
		setStickyState(rect.top <= STICKY_OFFSET);

		return true;
	};

	const initFallbackSticky = () => {
		if (!stickyTrigger) {
			return;
		}

		const evaluate = () => {
			if (!desktopBehaviorEnabled) {
				stickyFallbackTicking = false;
				return;
			}
			const rect = stickyTrigger.getBoundingClientRect();
			setStickyState(rect.top <= STICKY_OFFSET);
			stickyFallbackTicking = false;
		};

		const requestTick = () => {
			if (stickyFallbackTicking) {
				return;
			}
			stickyFallbackTicking = true;
			window.requestAnimationFrame(evaluate);
		};

		stickyFallbackScrollHandler = () => {
			requestTick();
		};

		stickyFallbackResizeHandler = () => {
			stickyFallbackTicking = false;
			requestTick();
		};

		window.addEventListener("scroll", stickyFallbackScrollHandler, { passive: true });
		window.addEventListener("resize", stickyFallbackResizeHandler);

		evaluate();
	};

	const enableDesktopBehavior = () => {
		moveCtaToOriginalPosition();
		desktopBehaviorEnabled = true;
		stickyActive = false;
		coverProgress = 0;
		heroVisible = isElementInViewport(hero);
		resetCoverProgress();
		cta.classList.remove("is-fixed");
		cta.classList.add("is-hidden");

		observeHeroVisibility();
		observeTrigger();

		if (!initScrollMagicSticky()) {
			initFallbackSticky();
		}

		const initialSticky = stickyTrigger ? stickyTrigger.getBoundingClientRect().top <= STICKY_OFFSET : true;
		setStickyState(initialSticky);
		updateVisibility();
	};

	const disableDesktopBehavior = () => {
		if (desktopBehaviorEnabled) {
			cleanupHeroVisibility();
			cleanupTriggerVisibility();
			cleanupStickyTracking();
		}

		desktopBehaviorEnabled = false;
		stickyActive = false;
		coverProgress = 0;
		heroVisible = true;
		resetCoverProgress();

		cta.classList.remove("is-fixed");
		cta.classList.remove("is-hidden");

		moveCtaToHero();
	};

	const syncLayout = () => {
		const shouldBeDesktop = isDesktopViewport();
		if (shouldBeDesktop === currentLayoutIsDesktop) {
			return;
		}
		currentLayoutIsDesktop = shouldBeDesktop;

		if (shouldBeDesktop) {
			enableDesktopBehavior();
		} else {
			disableDesktopBehavior();
		}
	};

	syncLayout();

	window.addEventListener("resize", () => {
		syncLayout();
	});
});
